import { matchPath } from 'react-router-dom';

import { SurveyValidationResult } from '../../launch/data/constants';
import { internalLinks, TW_URL_SURVEY_ID, TQS_URL_SURVEY_ID, URL_SURVEY_ID, URL_TEMPLATE_ID } from './constants';
import type { BreadCrumbLink } from './models';

export function filterVisibleItems(items: BreadCrumbLink[]): BreadCrumbLink[] {
    return items.filter(item => item.isVisible);
}

export type ToggleLink = {
    id: number;
    isDisabled: boolean;
};

export const getNewLinksArray = (breadcrumbsLinks: BreadCrumbLink[], toggleLinksArray: ToggleLink[]) => {
    if (Array.isArray(breadcrumbsLinks) && Array.isArray(toggleLinksArray)) {
        // return new links array with updated isDisabled
        return breadcrumbsLinks.map(breadcrumb => {
            const toggleLink = toggleLinksArray.find(link => breadcrumb.positionId === link.id);

            if (toggleLink) {
                return { ...breadcrumb, isDisabled: toggleLink.isDisabled };
            }

            return breadcrumb;
        });
    }

    return breadcrumbsLinks;
};

export const getUrlParam = (param: string) => {
    const query = window.location.search;
    const params = new URLSearchParams(query);
    return params && params.get(param);
};

enum RedirectLinkType {
    Define = 'define',
    Target = 'target',
    Build = 'build',
    Theme = 'theme',
    OldBuild = 'wizard/build',
}

type GetRedirectUrlParams = {
    surveyId: number;
    isHosted: boolean;
    hasNewBuildLabel: boolean;
    breadcrumbs: BreadCrumbLink[];
    validationResult?: SurveyValidationResult;
};

type RedirectUrl =
    | undefined
    | {
          link?: string;
          pathname?: string;
          search?: string;
      };

export const getCurrentDomain = () => {
    if (window.ts?.globals?.COOKIE_DOMAIN) {
        return window.ts.globals.COOKIE_DOMAIN.replace(/^\./, '');
    }

    const urlParts = window.location.hostname.split('.');
    // Get the domain name from the url.
    return urlParts.length === 1 ? urlParts[0] : urlParts.slice(Math.max(urlParts.length - 2, 1)).join('.');
};

export const getRedirectUrl = (params: GetRedirectUrlParams): RedirectUrl => {
    const { surveyId, isHosted, hasNewBuildLabel, breadcrumbs, validationResult } = params;

    if (!validationResult) {
        return;
    }

    // we need to check possibility of redirecting to each of the pages in breadcrumbs
    const redirectLinksMap = breadcrumbs.reduce<Partial<Record<RedirectLinkType, boolean>>>((acc, b) => {
        switch (true) {
            case b.linkTo.includes(RedirectLinkType.Define):
                acc[RedirectLinkType.Define] = true;
                break;
            case b.linkTo.includes(RedirectLinkType.Target):
                acc[RedirectLinkType.Target] = true;
                break;
            case b.linkTo.includes(RedirectLinkType.Build):
                acc[RedirectLinkType.Build] = true;
                break;
            case b.linkTo.includes(RedirectLinkType.Theme):
                acc[RedirectLinkType.Theme] = true;
                break;
        }
        return acc;
    }, {});

    const currentDomain = getCurrentDomain();

    switch (validationResult) {
        case SurveyValidationResult.MISSING_TARGET:
            return redirectLinksMap.target
                ? isHosted
                    ? {
                          pathname: `/${RedirectLinkType.Target}`,
                          search: `tqssurveyid=${surveyId}&launchSurveyId=${surveyId}`,
                      }
                    : {
                          link: `https://${currentDomain}/${RedirectLinkType.Target}?tqssurveyid=${surveyId}&launchSurveyId=${surveyId}`,
                      }
                : undefined;

        case SurveyValidationResult.TRANSLATION_MISSING:
        case SurveyValidationResult.SURVEY_STRUCTURE_INVALID:
        case SurveyValidationResult.UNKNOWN:
            if (!redirectLinksMap.build) {
                return;
            }

            if (isHosted && hasNewBuildLabel) {
                return {
                    pathname: `/${RedirectLinkType.Build}/${surveyId}`,
                    search: `launchSurveyId=${surveyId}`,
                };
            } else {
                return {
                    link: `https://project.${currentDomain}/${RedirectLinkType.OldBuild}/${surveyId}?launchSurveyId=${surveyId}`,
                };
            }
    }
};

export const getIsNewBuild = () => {
    const value = getUrlParam('isNew');
    return Boolean(value?.toString().toLowerCase().match(/^(true|yes|1|t|y)$/i));
};

export const getCurrentStep = () => {
    // Remove double slashes from the URL
    // e.g. //define/2762421 -> /define/2762421
    const pathUrl = window.location.pathname.replace(/\/{2,}/g, '/').toLowerCase();
    switch (true) {
        case pathUrl.includes(internalLinks.TEMPLATE_WIZARD.NAME): {
            return {
                success: true,
                data: {
                    step: internalLinks.TEMPLATE_WIZARD.STEP,
                    surveyId: getUrlParam(TW_URL_SURVEY_ID) ?? getUrlParam(URL_SURVEY_ID),
                    templateId: getUrlParam(URL_TEMPLATE_ID),
                },
            } as const;
        }
        case pathUrl.includes(internalLinks.DEFINE.NAME): {
            return {
                success: true,
                data: {
                    step: internalLinks.DEFINE.STEP,
                    surveyId: matchPath<{ surveyId: string }>(pathUrl, { path: '/define/:surveyId' })?.params.surveyId,
                    templateId: null,
                },
            } as const;
        }
        case pathUrl.includes(internalLinks.BUILD.NAME): {
            return {
                success: true,
                data: {
                    step: internalLinks.BUILD.STEP,
                    surveyId: matchPath<{ surveyId: string }>(pathUrl, { path: '/build/:surveyId' })?.params.surveyId,
                    templateId: null,
                },
            } as const;
        }
        case pathUrl.includes(internalLinks.TARGET.NAME): {
            return {
                success: true,
                data: {
                    step: internalLinks.TARGET.STEP,
                    surveyId: getUrlParam(TQS_URL_SURVEY_ID),
                    templateId: null,
                },
            } as const;
        }
        default: {
            return { success: false } as const;
        }
    }
};
